import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "reasonSelect", 
    "fileInput", 
    "fileInfo", 
    "fileName", 
    "notesSelect",
    "summaryTimestamp",
    "summaryReason", 
    "summaryReasonContainer",
    "summaryFile",
    "summaryFileContainer", 
    "summaryNote",
    "summaryNotesContainer",
    "dropZone"
  ]
  
  static values = { 
    reportId: String,
    alertId: String
  }

  declare readonly reasonSelectTarget: HTMLSelectElement
  declare readonly fileInputTarget: HTMLInputElement
  declare readonly fileInfoTarget: HTMLElement
  declare readonly fileNameTarget: HTMLElement
  declare readonly notesSelectTarget: HTMLSelectElement
  declare readonly summaryTimestampTarget: HTMLElement
  declare readonly summaryReasonTarget: HTMLElement
  declare readonly summaryReasonContainerTarget: HTMLElement
  declare readonly summaryFileTarget: HTMLElement
  declare readonly summaryFileContainerTarget: HTMLElement
  declare readonly summaryNoteTarget: HTMLElement
  declare readonly summaryNotesContainerTarget: HTMLElement
  declare readonly dropZoneTarget: HTMLElement
  
  declare readonly reportIdValue: string
  declare readonly alertIdValue: string

  connect() {
    this.updateSummaryTimestamp()
    this.setupEventListeners()
  }

  disconnect() {
    this.removeEventListeners()
  }

  private setupEventListeners() {
    this.reasonSelectTarget.addEventListener('change', this.updateSummary.bind(this))
    this.notesSelectTarget.addEventListener('change', this.updateSummary.bind(this))
    
    // Drag and drop event listeners
    this.dropZoneTarget.addEventListener('dragover', this.handleDragOver.bind(this))
    this.dropZoneTarget.addEventListener('dragenter', this.handleDragEnter.bind(this))
    this.dropZoneTarget.addEventListener('dragleave', this.handleDragLeave.bind(this))
    this.dropZoneTarget.addEventListener('drop', this.handleDrop.bind(this))
  }

  private removeEventListeners() {
    this.reasonSelectTarget.removeEventListener('change', this.updateSummary.bind(this))
    this.notesSelectTarget.removeEventListener('change', this.updateSummary.bind(this))
    
    // Remove drag and drop event listeners
    this.dropZoneTarget.removeEventListener('dragover', this.handleDragOver.bind(this))
    this.dropZoneTarget.removeEventListener('dragenter', this.handleDragEnter.bind(this))
    this.dropZoneTarget.removeEventListener('dragleave', this.handleDragLeave.bind(this))
    this.dropZoneTarget.removeEventListener('drop', this.handleDrop.bind(this))
  }

  private handleFileSelect(event: Event) {
    const target = event.target as HTMLInputElement
    const file = target.files?.[0]
    
    if (file) {
      if (this.validateFile(file)) {
        this.setSelectedFile(file)
      }
    } else {
      this.clearSelectedFile()
    }
  }

  // Drag and drop handlers
  private handleDragOver(event: DragEvent) {
    event.preventDefault()
    event.stopPropagation()
    this.dropZoneTarget.classList.add('border-blue-400', 'bg-blue-50')
  }

  private handleDragEnter(event: DragEvent) {
    event.preventDefault()
    event.stopPropagation()
    this.dropZoneTarget.classList.add('border-blue-400', 'bg-blue-50')
  }

  private handleDragLeave(event: DragEvent) {
    event.preventDefault()
    event.stopPropagation()
    this.dropZoneTarget.classList.remove('border-blue-400', 'bg-blue-50')
  }

  private handleDrop(event: DragEvent) {
    event.preventDefault()
    event.stopPropagation()
    this.dropZoneTarget.classList.remove('border-blue-400', 'bg-blue-50')
    
    const files = event.dataTransfer?.files
    if (files && files.length > 0) {
      const file = files[0]
      if (this.validateFile(file)) {
        this.setSelectedFile(file)
      }
    }
  }

  // File validation
  private validateFile(file: File): boolean {
    const maxSize = 10 * 1024 * 1024 // 10MB
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'image/jpeg',
      'image/jpg', 
      'image/png',
      'image/gif'
    ]
    
    if (file.size > maxSize) {
      alert('File size must be less than 10MB')
      return false
    }
    
    if (!allowedTypes.includes(file.type)) {
      alert('File type not supported. Please upload PDF, DOC, DOCX, TXT, or image files.')
      return false
    }
    
    return true
  }

  // File management helpers
  private setSelectedFile(file: File) {
    this.fileNameTarget.textContent = file.name
    this.fileInfoTarget.classList.remove('hidden')
    this.updateSummary()
  }

  private clearSelectedFile() {
    this.fileNameTarget.textContent = ''
    this.fileInfoTarget.classList.add('hidden')
    this.updateSummary()
  }

  private updateSummary() {
    // Update reason in summary
    const reason = this.reasonSelectTarget.value
    if (reason) {
      this.summaryReasonTarget.textContent = reason
      this.summaryReasonContainerTarget.style.display = 'flex'
    } else {
      this.summaryReasonContainerTarget.style.display = 'none'
    }

    // Update file in summary
    const fileName = this.fileNameTarget.textContent
    if (fileName) {
      this.summaryFileTarget.textContent = fileName
      this.summaryFileContainerTarget.style.display = 'flex'
    } else {
      this.summaryFileContainerTarget.style.display = 'none'
    }

    // Update notes in summary
    const note = this.notesSelectTarget.value
    if (note) {
      this.summaryNoteTarget.textContent = note
      this.summaryNotesContainerTarget.style.display = 'flex'
    } else {
      this.summaryNotesContainerTarget.style.display = 'none'
    }
  }

  private updateSummaryTimestamp() {
    const now = new Date()
    const timestamp = now.toLocaleString('en-US', {
      month: 'numeric',
      day: 'numeric', 
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    })
    this.summaryTimestampTarget.textContent = timestamp
  }

  clearAlert(event: Event) {
    event.preventDefault()
    
    const reason = this.reasonSelectTarget.value
    if (!reason) {
      alert('Please select a clearance reason.')
      return
    }

    const clearanceData = {
      reportId: this.reportIdValue,
      alertId: this.alertIdValue,
      reason,
      fileName: this.fileNameTarget.textContent || null,
      note: this.notesSelectTarget.value || null,
      timestamp: this.summaryTimestampTarget.textContent,
      clearedBy: 'Current User'
    }

    // Store cleared alert in localStorage/cookie
    this.storeClearedAlert(clearanceData)
    
    // Remove alert from UI
    this.removeAlertFromUI()
    
    // Close modal
    this.closeModal()
    
    // Show success message
    this.showSuccessMessage()
  }

  private storeClearedAlert(clearanceData: any) {
    // Use DemoStateManager if available for proper score calculation
    if (typeof (window as any).DemoStateManager !== 'undefined') {
      const DemoStateManager = (window as any).DemoStateManager
      const updatedScores = DemoStateManager.storeClearedAlert(this.reportIdValue, clearanceData)
      
      if (updatedScores) {
        // Trigger score updates across all components
        DemoStateManager.triggerScoreUpdates(this.reportIdValue)
      }
    } else {
      // Fallback: store directly to localStorage (for backwards compatibility)
      const storageKey = `clearedAlerts_${this.reportIdValue}`
      const existing = JSON.parse(localStorage.getItem(storageKey) || '[]')
      existing.push(clearanceData)
      localStorage.setItem(storageKey, JSON.stringify(existing))
    }
  }

  private removeAlertFromUI() {
    // Find the alert container and remove it
    const alertElement = document.querySelector(`[data-alert-id="${this.alertIdValue}"]`)
    if (alertElement) {
      alertElement.remove()
      
      // Update the alert count
      this.updateAlertCount()
    }
  }

  private updateAlertCount() {
    const alertCountElements = document.querySelectorAll('[data-alert-count]')
    alertCountElements.forEach(element => {
      const currentCount = parseInt(element.textContent || '0')
      if (currentCount > 0) {
        element.textContent = (currentCount - 1).toString()
      }
    })
  }

  private closeModal() {
    const dialog = this.element.closest('dialog') as HTMLDialogElement
    if (dialog) {
      dialog.close()
    }
  }

  private showSuccessMessage() {
    // Create and show a temporary success message
    const message = document.createElement('div')
    message.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50'
    message.textContent = 'Alert cleared successfully'
    document.body.appendChild(message)
    
    setTimeout(() => {
      message.remove()
    }, 3000)
  }

  // Static method to check if alert is cleared
  static isAlertCleared(reportId: string, alertId: string): boolean {
    const storageKey = `clearedAlerts_${reportId}`
    const clearedAlerts = JSON.parse(localStorage.getItem(storageKey) || '[]')
    return clearedAlerts.some((alert: any) => alert.alertId === alertId)
  }

  // Static method to get all cleared alerts for a report
  static getClearedAlerts(reportId: string): any[] {
    const storageKey = `clearedAlerts_${reportId}`
    return JSON.parse(localStorage.getItem(storageKey) || '[]')
  }
}